import { findAndUpdate } from "../utils/index.jsx";
import useResources from "./use-resources";
import useProperties from "./use-properties";
import useProfileFilter from "./use-profile-filter";
import { useCallback, useMemo } from "react";

const ROOT_PATH = "tenants";

export default function useTenants(options = {}) {
  const { isLocataire = false, status, withById = false } = options;
  const {
    resources: allTenants,
    resourcesById: allTenantsById,
    error,
    isLoading,
    create,
    update,
    del,
    mutate,
    axios,
  } = useResources(
    ROOT_PATH,
    {
      params: { is_locataire: isLocataire, status },
    },
    { withById },
  );

  const { isFilterActive } = useProfileFilter();
  const { properties: allowedProperties } = useProperties({
    noFetch: !isFilterActive || isLocataire,
  });

  const tenants = useMemo(() => {
    if (!isFilterActive || isLocataire) return allTenants;
    const allowedPropertyIds = new Set(allowedProperties.map((p) => p.id));
    return allTenants.filter((tenant) =>
      allowedPropertyIds.has(tenant.id_bien),
    );
  }, [allTenants, allowedProperties, isFilterActive, isLocataire]);

  const tenantsById = useMemo(() => {
    if (!withById) return allTenantsById;
    if (!isFilterActive || isLocataire) return allTenantsById;
    return tenants.reduce((acc, tenant) => {
      acc[tenant.id] = tenant;
      return acc;
    }, {});
  }, [tenants, allTenantsById, withById, isFilterActive, isLocataire]);

  function createTenant(tenant, profiles, otherValues = {}) {
    return create(
      {
        tenant,
        profiles,
        ...otherValues,
      },
      { shouldMutate: !otherValues.preview },
    );
  }

  async function createCandidat(propertyId, profiles, debut) {
    const response = await axios.post(
      `biens/${propertyId}/candidat?as_owner=true`,
      {
        profiles,
        debut,
      },
    );
    await mutate();
    return response.data;
  }

  function convertTenant(tenantId, tenant, profiles, otherValues = {}) {
    return convertOrRecreate(
      `${ROOT_PATH}/${tenantId}/convert`,
      tenantId,
      tenant,
      profiles,
      otherValues,
    );
  }

  function recreateTenant(tenantId, tenant, profiles, otherValues = {}) {
    return convertOrRecreate(
      `${ROOT_PATH}/${tenantId}/recreate`,
      tenantId,
      tenant,
      profiles,
      otherValues,
    );
  }

  async function convertOrRecreate(
    url,
    tenantId,
    tenant,
    profiles,
    otherValues = {},
  ) {
    let newResource;
    async function makeRequest() {
      const response = await axios.put(url, {
        tenant,
        profiles,
        ...otherValues,
      });
      return response.data;
    }
    if (!otherValues.preview) {
      await mutate(
        async (current) => {
          newResource = await makeRequest();
          return findAndUpdate(
            current,
            (resource) => resource.id === tenantId,
            newResource,
          );
        },
        { revalidate: false },
      );
    } else {
      newResource = await makeRequest();
    }
    return newResource;
  }

  const createCourrier = useCallback(
    async (tenantId, courrier, preview) => {
      let responseData;
      await mutate(
        async (currentTenants) => {
          const response = await axios.post(`/tenants/${tenantId}/courrier`, {
            ...courrier,
            preview,
          });

          responseData = response.data;
          if (preview) return currentTenants;
          return findAndUpdate(
            currentTenants,
            (tenant) => tenant.id === tenantId,
            responseData,
          );
        },
        { revalidate: false },
      );
      return responseData;
    },
    [axios, mutate],
  );

  const generateAllQuittances = useCallback(async () => {
    await axios.get("/tenants/generate-all-quittances");
    await mutate();
  }, [axios, mutate]);

  const bulkCreateAvisEcheance = useCallback(
    async ({ tenantIds, periode, sendEmail }) => {
      const response = await axios.post(
        "/tenants/bulk/courrier/avis-echeance",
        {
          tenant_ids: tenantIds,
          periode,
          send_email: sendEmail,
        },
      );
      return response.data;
    },
    [axios],
  );

  const previewAvisEcheancePdf = useCallback(
    async ({ tenantId, periode }) => {
      const previewResponse = await axios.post(
        `/tenants/${tenantId}/courrier/avis-echeance/preview`,
        { periode },
      );
      const blobResponse = await axios.get(previewResponse.data.download_url, {
        responseType: "blob",
      });
      return URL.createObjectURL(blobResponse.data);
    },
    [axios],
  );

  const bulkComputeAvisEcheance = useCallback(
    async ({ tenantIds, periode }) => {
      const response = await axios.post(
        "/tenants/bulk/courrier/avis-echeance/compute",
        { tenant_ids: tenantIds, periode },
      );
      return response.data;
    },
    [axios],
  );

  const getAttestationCafDirectPayment = useCallback(
    async (tenantId) => {
      const response = await axios.get(
        `/tenants/${tenantId}/attestation-caf/direct-payment`,
      );
      return response.data;
    },
    [axios],
  );

  const previewAttestationCafPdf = useCallback(
    async ({ tenantId, percevoirDirect, isChambre, directPayment }) => {
      const previewResponse = await axios.post(
        `/tenants/${tenantId}/attestation-caf/preview`,
        {
          percevoir_direct: percevoirDirect,
          is_chambre: isChambre,
          direct_payment: directPayment,
        },
      );
      const blobResponse = await axios.get(previewResponse.data.download_url, {
        responseType: "blob",
      });
      return URL.createObjectURL(blobResponse.data);
    },
    [axios],
  );

  const createAttestationCaf = useCallback(
    async ({ tenantId, percevoirDirect, isChambre, directPayment }) => {
      const response = await axios.post(
        `/tenants/${tenantId}/attestation-caf`,
        {
          percevoir_direct: percevoirDirect,
          is_chambre: isChambre,
          direct_payment: directPayment,
        },
      );
      return response.data;
    },
    [axios],
  );

  const bulkRevisionLoyer = useCallback(
    async ({ tenantIds, sendEmail }) => {
      const response = await axios.post("/tenants/bulk/courrier/revision", {
        tenant_ids: tenantIds,
        send_email: sendEmail,
      });
      await mutate();
      return response.data;
    },
    [axios, mutate],
  );

  const bulkComputeRevisionLoyer = useCallback(
    async ({ tenantIds }) => {
      const response = await axios.post(
        "/tenants/bulk/courrier/revision/compute",
        { tenant_ids: tenantIds },
      );
      return response.data;
    },
    [axios],
  );

  const previewRevisionLoyerPdf = useCallback(
    async ({ tenantId }) => {
      const previewResponse = await axios.post(
        `/tenants/${tenantId}/courrier/revision/preview`,
      );
      const blobResponse = await axios.get(previewResponse.data.download_url, {
        responseType: "blob",
      });
      return URL.createObjectURL(blobResponse.data);
    },
    [axios],
  );

  const bulkCreateCourrier = useCallback(
    async ({
      tenantIds,
      courrierStaticId,
      courrierContent,
      typeEnvoi,
      files = [],
    }) => {
      const response = await axios.post("/tenants/bulk/courrier", {
        tenant_ids: tenantIds,
        courrier_static_id: courrierStaticId,
        courrier_content: courrierContent,
        type_envoi: typeEnvoi,
        files,
      });
      await mutate();
      return response.data;
    },
    [axios, mutate],
  );

  const previewCourrierPdf = useCallback(
    async ({ tenantId, courrierStaticId, courrierContent }) => {
      const previewResponse = await axios.post(
        `/tenants/${tenantId}/courrier/preview`,
        {
          courrier_static_id: courrierStaticId,
          courrier_content: courrierContent,
        },
      );
      const blobResponse = await axios.get(previewResponse.data.download_url, {
        responseType: "blob",
      });
      return URL.createObjectURL(blobResponse.data);
    },
    [axios],
  );

  async function declineCandidat(tenantId, notifyCandidat) {
    const response = await axios.post(`${ROOT_PATH}/${tenantId}/decline`, {
      notify_candidat: notifyCandidat,
    });
    await mutate();
    return response.data;
  }

  async function withdrawCandidature(tenantId) {
    await axios.post(`${ROOT_PATH}/${tenantId}/withdraw`);
    await mutate();
  }

  async function upToDateAllTenants(tenantIds) {
    await axios.post(`${ROOT_PATH}/balances/up-to-date`, {
      tenant_ids: tenantIds,
    });
  }

  async function updateProrataTenant(tenantId) {
    await axios.post(`/tenants/${tenantId}/update-prorata`);
  }

  return {
    tenants,
    tenantsById,
    error,
    isLoading,
    mutate,
    createTenant,
    createCandidat,
    declineCandidat,
    withdrawCandidature,
    recreateTenant,
    convertTenant,
    updateTenant: update,
    deleteTenant: del,
    createCourrier,
    upToDateAllTenants,
    generateAllQuittances,
    bulkCreateAvisEcheance,
    previewAvisEcheancePdf,
    bulkComputeAvisEcheance,
    getAttestationCafDirectPayment,
    previewAttestationCafPdf,
    createAttestationCaf,
    bulkRevisionLoyer,
    bulkComputeRevisionLoyer,
    previewRevisionLoyerPdf,
    bulkCreateCourrier,
    previewCourrierPdf,
    updateProrataTenant,
  };
}
